/**
 * Elite Batch 22 Part 3 — 13 Marketing Workflow Templates
 * WebEngage (26-32): Referral Invite Journey, Quiz Result Personalization,
 *   App Uninstall Rescue, Push-to-Email Fallback Journey, Festival Sale Countdown,
 *   Order Shipment + Feedback Journey, RFM High-Value Customer Nurture
 * Klaviyo (33-38): Welcome Series Email, Welcome Series Email + SMS,
 *   First Purchase Discount Reminder, Abandoned Cart Reminder,
 *   High AOV Cart Rescue, Started Checkout Recovery
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

/* ───────────────────────────────────────────────────────────────────────────
 * 26. REFERRAL INVITE JOURNEY — WebEngage | Pattern J (Advocacy/Referral)
 * ─────────────────────────────────────────────────────────────────────────── */

const referral_invite_journey = {
  nodes: [
    trigger("Webhook: WebEngage Positive Customer Signal", "webhook_app", {
      source: "webengage",
      event: "positive_signal_detected",
      howItStarts:
        "Fires when WebEngage detects a positive customer signal such as a high NPS score, 5-star review, or repeat purchase milestone.",
      setup:
        "Configure WebEngage to send a webhook when a user submits a high NPS (9-10), leaves a positive review, or crosses a purchase milestone.",
      inputFields: [],
    }),
    aiNode(
      "2",
      "AI Extract Customer Health and Advocacy Eligibility",
      280,
      "You are a referral program analyst. Extract the customer email, name, signal type (nps, review, milestone), signal value (e.g. NPS score, star rating, milestone count), last purchase date, total order count, and whether they have been asked for a referral in the last 90 days (prior_referral_ask). Output valid JSON.",
      "Analyze this positive customer signal and determine referral eligibility: {webhook_payload}",
      [
        { field: "customer_email", as: "customer_email", type: "string" },
        { field: "customer_name", as: "customer_name", type: "string" },
        { field: "signal_type", as: "signal_type", type: "string" },
        { field: "signal_value", as: "signal_value", type: "string" },
        { field: "total_orders", as: "total_orders", type: "number" },
        {
          field: "prior_referral_ask",
          as: "prior_referral_ask",
          type: "string",
        },
      ],
    ),
    n("3", "hitApi", "WebEngage: Lookup Customer Health Score", 520, {
      hitApiDetails: {
        method: "GET",
        url: "https://api.webengage.com/v2/accounts/{webengage_account_id}/users/{customer_email}",
        headers: [
          { key: "Authorization", value: "Bearer {webengage_api_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: [
          {
            name: "users[0].attributes.we_health_score",
            customName: "health_score",
            type: "number",
          },
          {
            name: "users[0].attributes.we_referral_count",
            customName: "past_referrals",
            type: "number",
          },
          {
            name: "users[0].attributes.we_last_referral_date",
            customName: "last_referral_date",
            type: "string",
          },
        ],
      },
    }),
    n("4", "condition", "Eligible: Not Asked in Last 90 Days?", 760, {
      conditionDetails: {
        conditions: [
          {
            variable: "{prior_referral_ask}",
            operator: "equals",
            value: "false",
          },
        ],
      },
    }),
    emailNode(
      "5",
      "Email: Personalized Referral Invite with Incentive",
      1000,
      "{customer_email}",
      "Share the love, {customer_name} — give $15, get $15",
      "Hi {customer_name},\\n\\nWe noticed you are enjoying your experience with us — thank you for being a loyal customer with {total_orders} orders!\\n\\nWe would love for you to share the love. Give your friends $15 off their first order, and you will receive $15 in store credit for every successful referral.\\n\\nYour unique referral link: https://shop.example.com/refer/{customer_email}\\n\\nShare it with friends, family, or colleagues — there is no limit to how many people you can refer.\\n\\nThank you for being an advocate!",
    ),
    n("6", "delayAction", "Wait 5 Days for Referral Activity", 1240, {
      delayDetails: { duration: 5, unit: "days" },
    }),
    n("7", "condition", "Referral Completed?", 1480, {
      conditionDetails: {
        conditions: [
          {
            variable: "{past_referrals}",
            operator: "greater_than",
            value: "0",
          },
        ],
      },
    }),
    emailNode(
      "8",
      "Email: Gentle Referral Reminder with Alternative Ask",
      1720,
      "{customer_email}",
      "Quick reminder: your referral link is waiting, {customer_name}",
      "Hi {customer_name},\\n\\nJust a friendly reminder — your referral link is still active and ready to share!\\n\\nGive friends $15 off: https://shop.example.com/refer/{customer_email}\\n\\nNot ready to refer? No worries — you can also help by leaving a quick review on Google or sharing your favorite product on social media. Every bit helps!\\n\\nThank you for your support.",
      400,
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5", "output_1"),
    e("5", "6"),
    e("6", "7"),
    e("7", "8", "output_2"),
  ],
  summary:
    "Advocacy-driven referral workflow triggered by positive customer signals in WebEngage (high NPS, 5-star review, or repeat purchase milestone). AI extracts signal details and eligibility, the WebEngage API verifies health score and prior advocacy history, a condition gates on recency to avoid ask fatigue, and the customer receives a personalized referral invite with a dual-sided incentive. After a 5-day wait, non-converters get a gentle reminder with an alternative ask (review or social share).",
};

/* ───────────────────────────────────────────────────────────────────────────
 * 27. QUIZ RESULT PERSONALIZATION — WebEngage | Pattern I (Content/Event Nurture)
 * ─────────────────────────────────────────────────────────────────────────── */

const quiz_result_personalization = {
  nodes: [
    trigger("Webhook: WebEngage Quiz Completed Event", "webhook_app", {
      source: "webengage",
      event: "quiz_completed",
      howItStarts:
        "Fires when a user completes an on-site or in-app product quiz, style quiz, or skin-care assessment through WebEngage.",
      setup:
        "Configure WebEngage to send a webhook when the custom event quiz_completed fires, including quiz responses and user context.",
      inputFields: [],
    }),
    aiNode(
      "2",
      "AI Extract Quiz Responses and Generate Recommendations",
      280,
      "You are a product recommendation engine. Extract the customer email, name, quiz type (style, skincare, fitness, nutrition), quiz answers as a comma-separated summary, and generate a personalized product category, top 3 product recommendation names, and a short personalization summary explaining why these products match. Output valid JSON.",
      "Analyze this quiz completion payload and generate personalized product recommendations: {webhook_payload}",
      [
        { field: "customer_email", as: "customer_email", type: "string" },
        { field: "customer_name", as: "customer_name", type: "string" },
        { field: "quiz_type", as: "quiz_type", type: "string" },
        { field: "quiz_summary", as: "quiz_summary", type: "string" },
        {
          field: "recommended_category",
          as: "recommended_category",
          type: "string",
        },
        { field: "top_products", as: "top_products", type: "string" },
        {
          field: "personalization_summary",
          as: "personalization_summary",
          type: "string",
        },
      ],
    ),
    n(
      "3",
      "hitApi",
      "WebEngage: Fetch User Lifecycle Stage and Purchase History",
      520,
      {
        hitApiDetails: {
          method: "GET",
          url: "https://api.webengage.com/v2/accounts/{webengage_account_id}/users/{customer_email}",
          headers: [
            { key: "Authorization", value: "Bearer {webengage_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "users[0].attributes.we_lifecycle_stage",
              customName: "lifecycle_stage",
              type: "string",
            },
            {
              name: "users[0].attributes.we_total_purchases",
              customName: "total_purchases",
              type: "number",
            },
            {
              name: "users[0].attributes.we_last_category_viewed",
              customName: "last_category",
              type: "string",
            },
          ],
        },
      },
    ),
    n("4", "condition", "Engaged User with Prior Purchases?", 760, {
      conditionDetails: {
        conditions: [
          {
            variable: "{total_purchases}",
            operator: "greater_than",
            value: "0",
          },
        ],
      },
    }),
    emailNode(
      "5",
      "Email: Personalized Quiz Results with Product Picks",
      1000,
      "{customer_email}",
      "Your {quiz_type} quiz results are in, {customer_name}!",
      "Hi {customer_name},\\n\\nThank you for completing our {quiz_type} quiz! Based on your answers, here is what we recommend for you:\\n\\n{personalization_summary}\\n\\nYour top product picks:\\n{top_products}\\n\\nBrowse your personalized collection: https://shop.example.com/quiz-results/{recommended_category}\\n\\nThese were hand-picked based on your unique preferences. We think you will love them!",
    ),
    n("6", "delayAction", "Wait 3 Days for Engagement", 1240, {
      delayDetails: { duration: 3, unit: "days" },
    }),
    n("7", "condition", "Visited Recommended Category Since Quiz?", 1480, {
      conditionDetails: {
        conditions: [
          {
            variable: "{last_category}",
            operator: "equals",
            value: "{recommended_category}",
          },
        ],
      },
    }),
    emailNode(
      "8",
      "Email: Progressive Nurture with Deeper Content",
      1720,
      "{customer_email}",
      "More picks just for you, {customer_name}",
      "Hi {customer_name},\\n\\nSince you loved your {quiz_type} quiz results, we curated even more options in {recommended_category} that match your profile.\\n\\nQuiz insight: {quiz_summary}\\n\\nExplore your extended collection: https://shop.example.com/quiz-results/{recommended_category}?extended=true\\n\\nPlus, use code QUIZPICK for 10% off any quiz-recommended product this week.\\n\\nHappy shopping!",
      400,
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5", "output_1"),
    e("5", "6"),
    e("6", "7"),
    e("7", "8", "output_2"),
  ],
  summary:
    "Content nurture workflow triggered when a user completes a product quiz in WebEngage. AI extracts quiz answers and generates personalized product recommendations with a summary. The WebEngage API fetches lifecycle stage and purchase history to segment engaged vs. new users. A personalized results email is sent with top picks and a deep link. After 3 days, users who have not browsed the recommended category receive a progressive follow-up with extended picks and an exclusive discount code.",
};

/* ───────────────────────────────────────────────────────────────────────────
 * 28. APP UNINSTALL RESCUE — WebEngage | Pattern G (Re-engagement/Winback)
 * ─────────────────────────────────────────────────────────────────────────── */

const app_uninstall_rescue = {
  nodes: [
    trigger("Schedule: WebEngage Daily App Uninstall Scan", "schedule", {
      schedule: "Every day at 10:00 AM",
      howItStarts:
        "Runs daily to scan for users who uninstalled the app in the last 24 hours, pulling from the WebEngage uninstall segment.",
      setup:
        "Create a WebEngage segment for users with the system event app_uninstalled in the last 24 hours. Schedule this workflow to run daily at 10:00 AM.",
      inputFields: [],
    }),
    n("2", "hitApi", "WebEngage: Fetch Recent App Uninstall Segment", 280, {
      hitApiDetails: {
        method: "POST",
        url: "https://api.webengage.com/v2/accounts/{webengage_account_id}/segments/{uninstall_segment_id}/users",
        headers: [
          { key: "Authorization", value: "Bearer {webengage_api_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          {
            key: "filters",
            value: '{"event":"app_uninstalled","timeframe":"last_24h"}',
          },
        ],
        variables: [
          {
            name: "users[0].email",
            customName: "customer_email",
            type: "string",
          },
          {
            name: "users[0].attributes.we_first_name",
            customName: "customer_name",
            type: "string",
          },
          {
            name: "users[0].attributes.we_total_purchases",
            customName: "total_purchases",
            type: "number",
          },
          {
            name: "users[0].attributes.we_last_activity_date",
            customName: "last_activity_date",
            type: "string",
          },
          {
            name: "users[0].attributes.we_ltv",
            customName: "customer_ltv",
            type: "number",
          },
        ],
      },
    }),
    aiNode(
      "3",
      "AI Analyze Uninstall Reason and Recommend Winback Angle",
      520,
      "You are a mobile app retention analyst. Based on the user profile (purchase history, LTV, last activity date), infer the likely uninstall reason (storage_cleanup, poor_experience, competitor, no_longer_needed, accidental) and recommend the best winback angle (value_reminder, exclusive_offer, feature_update, apology, lite_version). Also output a personalized subject line for the rescue email. Output valid JSON.",
      "Analyze why this user may have uninstalled: name={customer_name}, email={customer_email}, total_purchases={total_purchases}, ltv={customer_ltv}, last_active={last_activity_date}",
      [
        { field: "likely_reason", as: "likely_reason", type: "string" },
        { field: "winback_angle", as: "winback_angle", type: "string" },
        { field: "subject_line", as: "rescue_subject", type: "string" },
        { field: "is_high_value", as: "is_high_value", type: "string" },
      ],
    ),
    n("4", "condition", "High-Value Customer (LTV > $200)?", 760, {
      conditionDetails: {
        conditions: [
          { variable: "{is_high_value}", operator: "equals", value: "true" },
        ],
      },
    }),
    emailNode(
      "5",
      "Email: Personalized App Winback with Value Reminder",
      1000,
      "{customer_email}",
      "{rescue_subject}",
      "Hi {customer_name},\\n\\nWe noticed you recently removed our app, and we wanted to reach out personally.\\n\\nAs a valued customer with {total_purchases} orders, you have exclusive access to in-app-only deals, early access to new collections, and faster checkout.\\n\\nWe have also made recent improvements including faster load times, a cleaner interface, and better notifications.\\n\\nReinstall now and get 20% off your next in-app purchase: https://app.example.com/reinstall?ref=winback\\n\\nWe would love to have you back!",
    ),
    n("6", "delayAction", "Wait 7 Days for Reinstall", 1240, {
      delayDetails: { duration: 7, unit: "days" },
    }),
    n("7", "condition", "App Reinstalled?", 1480, {
      conditionDetails: {
        conditions: [
          {
            variable: "{likely_reason}",
            operator: "equals",
            value: "accidental",
          },
        ],
      },
    }),
    emailNode(
      "8",
      "Email: Last Chance Offer or Web Alternative",
      1720,
      "{customer_email}",
      "We miss you, {customer_name} — here is something special",
      "Hi {customer_name},\\n\\nWe understand the app might not be the right fit right now, and that is okay.\\n\\nHere is what you can still enjoy without the app:\\n- Browse and shop on our mobile website\\n- Get email-exclusive deals every week\\n- Use code COMEBACK25 for 25% off your next order (any channel)\\n\\nIf you ever want to reinstall: https://app.example.com/reinstall\\n\\nWe value your business no matter how you shop with us.",
      400,
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5", "output_1"),
    e("5", "6"),
    e("6", "7"),
    e("7", "8", "output_2"),
  ],
  summary:
    "Re-engagement workflow that runs daily to rescue users who uninstalled the mobile app. WebEngage provides the uninstall segment, AI analyzes likely uninstall reasons and recommends the best winback angle per user. High-value customers receive a personalized winback email with an exclusive reinstall incentive. After 7 days, those who have not reinstalled get a last-chance email offering a web alternative and a stronger discount code to retain them on any channel.",
};

/* ───────────────────────────────────────────────────────────────────────────
 * 29. PUSH-TO-EMAIL FALLBACK JOURNEY — WebEngage | Pattern A (Welcome/Onboarding)
 * ─────────────────────────────────────────────────────────────────────────── */

const push_to_email_fallback_journey = {
  nodes: [
    trigger("Webhook: WebEngage Push Notification Sent", "webhook_app", {
      source: "webengage",
      event: "push_sent",
      howItStarts:
        "Fires when WebEngage sends a push notification to a user, enabling fallback tracking if the push is not opened within a defined window.",
      setup:
        "Configure WebEngage to send a webhook on push_sent events, including the push content, user ID, and campaign context.",
      inputFields: [],
    }),
    aiNode(
      "2",
      "AI Extract Push Details and User Channel Preferences",
      280,
      "You are a channel optimization analyst. Extract the customer email, customer name, push title, push body, campaign name, device type (ios, android), push token status (active, expired), and the users preferred communication channel if known. Output valid JSON.",
      "Parse this push notification event and extract channel details: {webhook_payload}",
      [
        { field: "customer_email", as: "customer_email", type: "string" },
        { field: "customer_name", as: "customer_name", type: "string" },
        { field: "push_title", as: "push_title", type: "string" },
        { field: "push_body", as: "push_body", type: "string" },
        { field: "campaign_name", as: "campaign_name", type: "string" },
        { field: "device_type", as: "device_type", type: "string" },
        { field: "push_token_status", as: "push_token_status", type: "string" },
      ],
    ),
    n("3", "hitApi", "WebEngage: Check Push Open Status After Window", 520, {
      hitApiDetails: {
        method: "GET",
        url: "https://api.webengage.com/v2/accounts/{webengage_account_id}/campaigns/{campaign_name}/users/{customer_email}/status",
        headers: [
          { key: "Authorization", value: "Bearer {webengage_api_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: [
          {
            name: "status.push_opened",
            customName: "push_opened",
            type: "string",
          },
          {
            name: "status.delivery_status",
            customName: "delivery_status",
            type: "string",
          },
          {
            name: "status.email_subscribed",
            customName: "email_subscribed",
            type: "string",
          },
        ],
      },
    }),
    n("4", "condition", "Push Opened Within Window?", 760, {
      conditionDetails: {
        conditions: [
          { variable: "{push_opened}", operator: "equals", value: "true" },
        ],
      },
    }),
    emailNode(
      "5",
      "Email: Fallback with Same Campaign Content",
      1000,
      "{customer_email}",
      "{push_title}",
      "Hi {customer_name},\\n\\nWe wanted to make sure you saw this:\\n\\n{push_body}\\n\\nWe sent this as a push notification earlier but wanted to follow up via email to make sure it reached you.\\n\\nTap here to take action: https://shop.example.com/campaign/{campaign_name}\\n\\nYou can manage your notification preferences anytime in your account settings.",
    ),
    n("6", "delayAction", "Wait 24 Hours for Email Engagement", 1240, {
      delayDetails: { duration: 24, unit: "hours" },
    }),
    n("7", "condition", "Email Opened or Clicked?", 1480, {
      conditionDetails: {
        conditions: [
          { variable: "{email_subscribed}", operator: "equals", value: "true" },
        ],
      },
    }),
    emailNode(
      "8",
      "Email: Channel Preference Update Prompt",
      1720,
      "{customer_email}",
      "How would you like to hear from us, {customer_name}?",
      "Hi {customer_name},\\n\\nWe want to reach you on the channels you prefer. Currently, we are sending you push notifications and emails, but we noticed some of our messages may not be reaching you.\\n\\nUpdate your preferences to choose how you want to hear about:\\n- New product launches\\n- Exclusive offers\\n- Order updates\\n\\nUpdate preferences: https://shop.example.com/preferences\\n\\nThis way, you will always get the messages that matter most to you.",
      400,
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5", "output_2"),
    e("5", "6"),
    e("6", "7"),
    e("7", "8", "output_2"),
  ],
  summary:
    "Channel fallback workflow triggered when WebEngage sends a push notification. AI extracts push content and channel details, then the API checks whether the push was opened. If not opened, the same campaign content is delivered via email as a fallback. After 24 hours, if the email also did not engage, the user receives a preference update prompt so they can choose their preferred communication channel, improving future deliverability.",
};

/* ───────────────────────────────────────────────────────────────────────────
 * 30. FESTIVAL SALE COUNTDOWN — WebEngage | Pattern D (Catalog Alert)
 * ─────────────────────────────────────────────────────────────────────────── */

const festival_sale_countdown = {
  nodes: [
    trigger("Webhook: WebEngage Festival Sale Event Published", "webhook_app", {
      source: "webengage",
      event: "festival_sale_published",
      howItStarts:
        "Fires when a festival or seasonal sale is published in the system, triggering a countdown campaign to subscribed users.",
      setup:
        "Configure WebEngage to fire a webhook when a sale event is created with type festival_sale, including sale name, start/end dates, discount details, and target audience segment.",
      inputFields: [],
    }),
    aiNode(
      "2",
      "AI Extract Sale Details and Generate Countdown Messaging",
      280,
      "You are a marketing copywriter specializing in urgency-driven campaigns. Extract the sale name, start datetime, end datetime, headline discount percentage, featured categories, and target segment. Then generate a countdown subject line, a preview text, and the hours remaining until sale starts. Output valid JSON.",
      "Parse this festival sale event and generate countdown messaging: {webhook_payload}",
      [
        { field: "sale_name", as: "sale_name", type: "string" },
        { field: "start_date", as: "start_date", type: "string" },
        { field: "end_date", as: "end_date", type: "string" },
        { field: "discount_pct", as: "discount_pct", type: "string" },
        {
          field: "featured_categories",
          as: "featured_categories",
          type: "string",
        },
        { field: "countdown_subject", as: "countdown_subject", type: "string" },
        { field: "customer_email", as: "customer_email", type: "string" },
        { field: "customer_name", as: "customer_name", type: "string" },
      ],
    ),
    n("3", "hitApi", "WebEngage: Verify User Interest and Consent", 520, {
      hitApiDetails: {
        method: "GET",
        url: "https://api.webengage.com/v2/accounts/{webengage_account_id}/users/{customer_email}",
        headers: [
          { key: "Authorization", value: "Bearer {webengage_api_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: [
          {
            name: "users[0].attributes.we_preferred_categories",
            customName: "preferred_categories",
            type: "string",
          },
          {
            name: "users[0].attributes.we_marketing_consent",
            customName: "marketing_consent",
            type: "string",
          },
          {
            name: "users[0].attributes.we_last_purchase_date",
            customName: "last_purchase_date",
            type: "string",
          },
        ],
      },
    }),
    n("4", "condition", "High Urgency: Sale Starts Within 24h?", 760, {
      conditionDetails: {
        conditions: [
          { variable: "{discount_pct}", operator: "greater_than", value: "30" },
        ],
      },
    }),
    emailNode(
      "5",
      "Email: Festival Sale Countdown Alert with Featured Products",
      1000,
      "{customer_email}",
      "{countdown_subject}",
      "Hi {customer_name},\\n\\nThe {sale_name} is almost here!\\n\\nUp to {discount_pct}% off across {featured_categories}\\n\\nSale starts: {start_date}\\nSale ends: {end_date}\\n\\nAdd your favorites to your wishlist now so you are ready when the sale drops: https://shop.example.com/wishlist\\n\\nSet a reminder and be first in line for the best deals!\\n\\nShop early for the best selection.",
    ),
    n("6", "delayAction", "Wait 48 Hours Until Sale Midpoint", 1240, {
      delayDetails: { duration: 48, unit: "hours" },
    }),
    n("7", "condition", "User Has Not Purchased During Sale?", 1480, {
      conditionDetails: {
        conditions: [
          {
            variable: "{last_purchase_date}",
            operator: "less_than",
            value: "{start_date}",
          },
        ],
      },
    }),
    emailNode(
      "8",
      "Email: Last Chance Sale Urgency with Top Picks",
      1720,
      "{customer_email}",
      "Last chance: {sale_name} ends soon — do not miss {discount_pct}% off",
      "Hi {customer_name},\\n\\nTime is running out! The {sale_name} ends on {end_date}.\\n\\nYour preferred categories {preferred_categories} still have great deals:\\n\\nFeatured: {featured_categories}\\nUp to {discount_pct}% off\\n\\nDo not wait — the most popular items are selling fast.\\n\\nShop the sale now: https://shop.example.com/sale/{sale_name}\\n\\nThis is your last chance to grab these deals before they are gone.",
      400,
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5", "output_1"),
    e("5", "6"),
    e("6", "7"),
    e("7", "8", "output_1"),
  ],
  summary:
    "Catalog alert workflow for festival and seasonal sales in WebEngage. AI extracts sale details and generates urgency-driven countdown messaging. The WebEngage API verifies user interest via preferred categories and marketing consent. A countdown email goes out with featured products and wishlist prompts. After 48 hours at the sale midpoint, users who have not purchased receive a last-chance urgency email with their preferred categories highlighted and top remaining deals.",
};

/* ───────────────────────────────────────────────────────────────────────────
 * 31. ORDER SHIPMENT + FEEDBACK JOURNEY — WebEngage | Pattern E (Post-Purchase)
 * ─────────────────────────────────────────────────────────────────────────── */

const order_shipment_feedback_journey = {
  nodes: [
    trigger("Webhook: WebEngage Order Shipped Event", "webhook_app", {
      source: "webengage",
      event: "order_shipped",
      howItStarts:
        "Fires when an order is shipped and WebEngage receives the fulfillment event with tracking details.",
      setup:
        "Configure your fulfillment system or Shopify to send order shipped events to WebEngage, which forwards them as a webhook including order ID, tracking number, carrier, and estimated delivery date.",
      inputFields: [],
    }),
    aiNode(
      "2",
      "AI Extract Shipment Details and Delivery Estimate",
      280,
      "You are an order fulfillment assistant. Extract the order ID, customer email, customer name, tracking number, carrier name, estimated delivery date, items shipped (comma-separated product names), and whether this is the customers first purchase. Output valid JSON.",
      "Parse this order shipment event and extract all fulfillment details: {webhook_payload}",
      [
        { field: "order_id", as: "order_id", type: "string" },
        { field: "customer_email", as: "customer_email", type: "string" },
        { field: "customer_name", as: "customer_name", type: "string" },
        { field: "tracking_number", as: "tracking_number", type: "string" },
        { field: "carrier", as: "carrier", type: "string" },
        {
          field: "estimated_delivery",
          as: "estimated_delivery",
          type: "string",
        },
        { field: "items_shipped", as: "items_shipped", type: "string" },
        { field: "is_first_purchase", as: "is_first_purchase", type: "string" },
      ],
    ),
    n("3", "hitApi", "WebEngage: Lookup Customer Tenure and VIP Status", 520, {
      hitApiDetails: {
        method: "GET",
        url: "https://api.webengage.com/v2/accounts/{webengage_account_id}/users/{customer_email}",
        headers: [
          { key: "Authorization", value: "Bearer {webengage_api_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: [
          {
            name: "users[0].attributes.we_customer_since",
            customName: "customer_since",
            type: "string",
          },
          {
            name: "users[0].attributes.we_vip_tier",
            customName: "vip_tier",
            type: "string",
          },
          {
            name: "users[0].attributes.we_total_orders",
            customName: "total_orders",
            type: "number",
          },
        ],
      },
    }),
    n("4", "condition", "First Purchase?", 760, {
      conditionDetails: {
        conditions: [
          {
            variable: "{is_first_purchase}",
            operator: "equals",
            value: "true",
          },
        ],
      },
    }),
    emailNode(
      "5",
      "Email: Shipment Confirmation with Tracking and Care Tips",
      1000,
      "{customer_email}",
      "Your order #{order_id} is on its way, {customer_name}!",
      "Hi {customer_name},\\n\\nGreat news — your order has shipped!\\n\\nOrder #{order_id}\\nItems: {items_shipped}\\nCarrier: {carrier}\\nTracking: {tracking_number}\\nEstimated delivery: {estimated_delivery}\\n\\nTrack your package: https://shop.example.com/track/{tracking_number}\\n\\nWhile you wait, here are some tips to get the most out of your new items when they arrive.\\n\\nThank you for choosing us!",
    ),
    n("6", "delayAction", "Wait 7 Days for Delivery Window", 1240, {
      delayDetails: { duration: 7, unit: "days" },
    }),
    n("7", "condition", "Order Likely Delivered?", 1480, {
      conditionDetails: {
        conditions: [
          { variable: "{total_orders}", operator: "greater_than", value: "0" },
        ],
      },
    }),
    emailNode(
      "8",
      "Email: Delivery Feedback Request with Quick Rating",
      1720,
      "{customer_email}",
      "How was your delivery experience, {customer_name}?",
      "Hi {customer_name},\\n\\nYour order #{order_id} should have arrived by now. We hope you love your {items_shipped}!\\n\\nWe would love to hear about your experience. It takes just 30 seconds:\\n\\nRate your delivery: https://shop.example.com/feedback/{order_id}\\n\\n- Was the packaging in good condition?\\n- Did it arrive on time?\\n- How do you like the products?\\n\\nYour feedback helps us improve for everyone. As a thank you, you will receive 50 loyalty points for completing the survey.\\n\\nThank you for your support!",
      400,
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5", "output_1"),
    e("5", "6"),
    e("6", "7"),
    e("7", "8", "output_1"),
  ],
  summary:
    "Post-purchase workflow triggered when an order ships in WebEngage. AI extracts shipment details including tracking, carrier, and estimated delivery. The WebEngage API fetches customer tenure and VIP status. First-time buyers receive a shipment confirmation with care tips and tracking. After a 7-day delivery window, a feedback request email is sent with a quick rating form and a loyalty points incentive, driving review collection and delivery quality insights.",
};

/* ───────────────────────────────────────────────────────────────────────────
 * 32. RFM HIGH-VALUE CUSTOMER NURTURE — WebEngage | Pattern H (Milestone/Loyalty/VIP)
 * ─────────────────────────────────────────────────────────────────────────── */

const rfm_high_value_customer_nurture = {
  nodes: [
    trigger(
      "Schedule: WebEngage Weekly RFM High-Value Segment Scan",
      "schedule",
      {
        schedule: "Every Monday at 9:00 AM",
        howItStarts:
          "Runs weekly to identify customers in the top RFM (Recency, Frequency, Monetary) segment for exclusive nurture campaigns.",
        setup:
          "Create a WebEngage segment for users with RFM score in the top 10% (score >= 8). Schedule this workflow to run every Monday at 9:00 AM.",
        inputFields: [],
      },
    ),
    n("2", "hitApi", "WebEngage: Fetch High-RFM Customer Segment", 280, {
      hitApiDetails: {
        method: "POST",
        url: "https://api.webengage.com/v2/accounts/{webengage_account_id}/segments/{rfm_high_value_segment_id}/users",
        headers: [
          { key: "Authorization", value: "Bearer {webengage_api_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "filters", value: '{"rfm_score_min":8,"limit":100}' }],
        variables: [
          {
            name: "users[0].email",
            customName: "customer_email",
            type: "string",
          },
          {
            name: "users[0].attributes.we_first_name",
            customName: "customer_name",
            type: "string",
          },
          {
            name: "users[0].attributes.we_rfm_score",
            customName: "rfm_score",
            type: "number",
          },
          {
            name: "users[0].attributes.we_ltv",
            customName: "customer_ltv",
            type: "number",
          },
          {
            name: "users[0].attributes.we_vip_tier",
            customName: "vip_tier",
            type: "string",
          },
          {
            name: "users[0].attributes.we_total_orders",
            customName: "total_orders",
            type: "number",
          },
        ],
      },
    }),
    n("3", "condition", "Already in VIP Tier?", 520, {
      conditionDetails: {
        conditions: [
          { variable: "{vip_tier}", operator: "equals", value: "platinum" },
        ],
      },
    }),
    emailNode(
      "4",
      "Email: Exclusive VIP Offer with Early Access Perks",
      760,
      "{customer_email}",
      "Exclusive for you, {customer_name} — VIP early access inside",
      "Hi {customer_name},\\n\\nAs one of our most valued customers (RFM score: {rfm_score}/10), we are giving you exclusive early access to our upcoming collection before anyone else.\\n\\nYour VIP perks this week:\\n- Early access to new arrivals (48 hours before public launch)\\n- Free express shipping on all orders\\n- Personal styling consultation available\\n\\nShop early access now: https://shop.example.com/vip-access\\n\\nWith {total_orders} orders and counting, you are a core part of our community. Thank you for your continued loyalty!",
    ),
    n("5", "delayAction", "Wait 5 Days for VIP Engagement", 1000, {
      delayDetails: { duration: 5, unit: "days" },
    }),
    n("6", "condition", "VIP Reward Redeemed?", 1240, {
      conditionDetails: {
        conditions: [
          {
            variable: "{customer_ltv}",
            operator: "greater_than",
            value: "500",
          },
        ],
      },
    }),
    emailNode(
      "7",
      "Email: Redemption Reminder or Next-Tier Teaser",
      1480,
      "{customer_email}",
      "Your exclusive perks are waiting, {customer_name}",
      "Hi {customer_name},\\n\\nJust a reminder — your VIP early access and free express shipping are still available this week.\\n\\nDid you know? You are just a few orders away from unlocking our Diamond tier, which includes:\\n- Priority customer support\\n- Birthday surprise gifts\\n- Exclusive quarterly preview events\\n\\nKeep shopping to level up: https://shop.example.com/loyalty-tiers\\n\\nYour current tier: {vip_tier}\\nLifetime value: ${customer_ltv}\\nTotal orders: {total_orders}",
      400,
    ),
    sheetsNode(
      "8",
      "Log RFM High-Value Nurture Activity",
      1720,
      "RFM_VIP_Nurture",
      '[["{customer_email}","{customer_name}","{rfm_score}","{vip_tier}","{customer_ltv}","{total_orders}","=NOW()"]]',
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4", "output_1"),
    e("4", "5"),
    e("5", "6"),
    e("6", "7", "output_2"),
    e("7", "8"),
  ],
  summary:
    "Milestone and loyalty nurture workflow that runs weekly to identify top RFM-scored customers in WebEngage. The segment API pulls high-value users, a condition checks VIP tier status, and platinum members receive an exclusive early-access offer with premium perks. After 5 days, unredeemed users get a reminder with a next-tier teaser showing progress toward Diamond status. All nurture activity is logged to Google Sheets for VIP program tracking and LTV analysis.",
};

/* ───────────────────────────────────────────────────────────────────────────
 * 33. WELCOME SERIES EMAIL — Klaviyo | Pattern A (Welcome/Onboarding)
 * ─────────────────────────────────────────────────────────────────────────── */

const welcome_series_email = {
  nodes: [
    trigger("Webhook: Klaviyo Email List Signup", "webhook_app", {
      source: "klaviyo",
      event: "list_subscribed",
      howItStarts:
        "Fires when a new subscriber joins the email list through a Klaviyo signup form, popup, or embedded form on the website.",
      setup:
        "Configure Klaviyo to send a webhook when a profile subscribes to your main email list. Include profile properties and signup source.",
      inputFields: [],
    }),
    aiNode(
      "2",
      "AI Extract Subscriber Details and Signup Context",
      280,
      "You are a Klaviyo onboarding specialist. Extract the subscriber email, first name (or 'there' if unknown), signup source (popup, footer, checkout, landing_page), and any preferences or interests indicated during signup. Output valid JSON.",
      "Parse this Klaviyo signup event and extract subscriber details: {webhook_payload}",
      [
        { field: "subscriber_email", as: "subscriber_email", type: "string" },
        { field: "first_name", as: "first_name", type: "string" },
        { field: "signup_source", as: "signup_source", type: "string" },
        { field: "interests", as: "interests", type: "string" },
      ],
    ),
    n(
      "3",
      "hitApi",
      "Klaviyo: Lookup Profile for Existing Customer Check",
      520,
      {
        hitApiDetails: {
          method: "GET",
          url: 'https://a.klaviyo.com/api/profiles/?filter=equals(email,"{subscriber_email}")',
          headers: [
            {
              key: "Authorization",
              value: "Klaviyo-API-Key {klaviyo_private_key}",
            },
            { key: "Content-Type", value: "application/json" },
            { key: "revision", value: "2024-02-15" },
          ],
          body: [],
          variables: [
            {
              name: "data[0].attributes.properties.total_orders",
              customName: "total_orders",
              type: "number",
            },
            {
              name: "data[0].attributes.properties.first_purchase_date",
              customName: "first_purchase_date",
              type: "string",
            },
            {
              name: "data[0].id",
              customName: "klaviyo_profile_id",
              type: "string",
            },
          ],
        },
      },
    ),
    n("4", "condition", "Existing Customer with Prior Purchases?", 760, {
      conditionDetails: {
        conditions: [
          { variable: "{total_orders}", operator: "greater_than", value: "0" },
        ],
      },
    }),
    emailNode(
      "5",
      "Email: Welcome with Brand Story and Value Proposition",
      1000,
      "{subscriber_email}",
      "Welcome to the family, {first_name}!",
      "Hi {first_name},\\n\\nWelcome! We are thrilled you joined our community.\\n\\nHere is what you can expect from us:\\n- Exclusive early access to new arrivals\\n- Members-only discounts and flash sales\\n- Curated content and style guides\\n\\nTo get you started, here is 10% off your first order: WELCOME10\\n\\nShop now: https://shop.example.com?utm_source=klaviyo&utm_campaign=welcome\\n\\nNeed help finding the right product? Our team is here to help — just reply to this email.\\n\\nWelcome aboard!",
    ),
    n("6", "delayAction", "Wait 48 Hours for First Engagement", 1240, {
      delayDetails: { duration: 48, unit: "hours" },
    }),
    n("7", "condition", "Made First Purchase Since Signup?", 1480, {
      conditionDetails: {
        conditions: [
          {
            variable: "{first_purchase_date}",
            operator: "not_equals",
            value: "",
          },
        ],
      },
    }),
    emailNode(
      "8",
      "Email: Education Follow-Up with Bestsellers Push",
      1720,
      "{subscriber_email}",
      "Still exploring, {first_name}? Here are our bestsellers",
      "Hi {first_name},\\n\\nWe noticed you have not used your welcome discount yet — WELCOME10 is still waiting for you!\\n\\nNot sure where to start? Here are our most-loved products:\\n\\n1. Our #1 bestseller — loved by 10,000+ customers\\n2. Editor's pick — featured in this month's collection\\n3. New arrival — just dropped this week\\n\\nShop bestsellers: https://shop.example.com/bestsellers?utm_source=klaviyo&utm_campaign=welcome_followup\\n\\nRemember, your 10% discount expires in 5 days.\\n\\nHappy shopping!",
      400,
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5", "output_2"),
    e("5", "6"),
    e("6", "7"),
    e("7", "8", "output_2"),
  ],
  summary:
    "Klaviyo welcome series triggered when a new subscriber joins the email list. AI extracts subscriber details and signup context, then the Klaviyo API checks for existing purchase history. New subscribers receive a brand story welcome email with a 10% first-order discount. After 48 hours, those who have not purchased get a follow-up featuring bestsellers and a reminder that the welcome discount is expiring, pushing toward first purchase conversion.",
};

/* ───────────────────────────────────────────────────────────────────────────
 * 34. WELCOME SERIES EMAIL + SMS — Klaviyo | Pattern A (Welcome/Onboarding)
 * ─────────────────────────────────────────────────────────────────────────── */

const welcome_series_email_sms = {
  nodes: [
    trigger("Webhook: Klaviyo Dual Email + SMS Opt-In", "webhook_app", {
      source: "klaviyo",
      event: "dual_optin_completed",
      howItStarts:
        "Fires when a subscriber completes both email and SMS opt-in through a Klaviyo multi-step signup form.",
      setup:
        "Configure Klaviyo to send a webhook when a profile subscribes to both email and SMS lists simultaneously. Include phone number and consent details.",
      inputFields: [],
    }),
    aiNode(
      "2",
      "AI Extract Dual Opt-In Details and Channel Preferences",
      280,
      "You are a Klaviyo omnichannel onboarding specialist. Extract the subscriber email, phone number, first name, signup source, email consent status, sms consent status, and any product interests indicated. Output valid JSON.",
      "Parse this dual opt-in event and extract all subscriber channel details: {webhook_payload}",
      [
        { field: "subscriber_email", as: "subscriber_email", type: "string" },
        { field: "phone_number", as: "phone_number", type: "string" },
        { field: "first_name", as: "first_name", type: "string" },
        { field: "signup_source", as: "signup_source", type: "string" },
        { field: "email_consent", as: "email_consent", type: "string" },
        { field: "sms_consent", as: "sms_consent", type: "string" },
        { field: "product_interests", as: "product_interests", type: "string" },
      ],
    ),
    n("3", "hitApi", "Klaviyo: Lookup Profile for Purchase History", 520, {
      hitApiDetails: {
        method: "GET",
        url: 'https://a.klaviyo.com/api/profiles/?filter=equals(email,"{subscriber_email}")',
        headers: [
          {
            key: "Authorization",
            value: "Klaviyo-API-Key {klaviyo_private_key}",
          },
          { key: "Content-Type", value: "application/json" },
          { key: "revision", value: "2024-02-15" },
        ],
        body: [],
        variables: [
          {
            name: "data[0].attributes.properties.total_orders",
            customName: "total_orders",
            type: "number",
          },
          {
            name: "data[0].attributes.properties.lifetime_value",
            customName: "lifetime_value",
            type: "number",
          },
          {
            name: "data[0].id",
            customName: "klaviyo_profile_id",
            type: "string",
          },
        ],
      },
    }),
    n("4", "condition", "Returning Customer?", 760, {
      conditionDetails: {
        conditions: [
          { variable: "{total_orders}", operator: "greater_than", value: "0" },
        ],
      },
    }),
    emailNode(
      "5",
      "Email: Welcome with Brand Story + SMS Confirmation",
      1000,
      "{subscriber_email}",
      "Welcome, {first_name}! You are in the inner circle now",
      "Hi {first_name},\\n\\nWelcome to our community! Since you opted in for both email and SMS, you are now part of our inner circle with the best perks:\\n\\nEmail perks: Weekly curated picks, exclusive content, early sale access\\nSMS perks: Flash sale alerts, order updates, time-sensitive drops\\n\\nTo celebrate, here is 15% off your first order: INNER15\\n\\nShop now: https://shop.example.com?utm_source=klaviyo&utm_campaign=dual_welcome\\n\\nYou will also receive a welcome text shortly with a quick-shop link!",
    ),
    smsNode(
      "6",
      "SMS: Welcome Text with Quick-Shop Link",
      1240,
      "{phone_number}",
      "Welcome to the fam, {first_name}! You are in our inner circle. Use INNER15 for 15% off your first order: https://shop.example.com/s/welcome Reply STOP to opt out.",
    ),
    n("7", "condition", "First Purchase Made Within 48h?", 1480, {
      conditionDetails: {
        conditions: [
          {
            variable: "{lifetime_value}",
            operator: "greater_than",
            value: "0",
          },
        ],
      },
    }),
    emailNode(
      "8",
      "Email: Coordinated Follow-Up with Channel Benefits",
      1720,
      "{subscriber_email}",
      "Do not miss out, {first_name} — your 15% off expires soon",
      "Hi {first_name},\\n\\nJust a heads up — your exclusive INNER15 discount (15% off) expires in 3 days!\\n\\nSince you signed up for both email and SMS, here is what is coming your way this week:\\n- Tomorrow: SMS flash sale preview (SMS-only!)\\n- Wednesday: New arrivals email with your interests: {product_interests}\\n- Friday: Weekend deals roundup\\n\\nDo not miss your first-order discount: https://shop.example.com?code=INNER15\\n\\nWe are excited to have you!",
      400,
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5", "output_2"),
    e("5", "6"),
    e("6", "7"),
    e("7", "8", "output_2"),
  ],
  summary:
    "Coordinated Klaviyo welcome series for dual email + SMS opt-in subscribers. AI extracts channel preferences and consent details, the Klaviyo API checks purchase history. New subscribers receive a welcome email highlighting dual-channel perks with a 15% inner circle discount, immediately followed by a welcome SMS with a quick-shop link. After the SMS, those who have not purchased within 48 hours get a follow-up email previewing the upcoming week's content calendar and a discount expiry reminder.",
};

/* ───────────────────────────────────────────────────────────────────────────
 * 35. FIRST PURCHASE DISCOUNT REMINDER — Klaviyo | Pattern A (Welcome/Onboarding)
 * ─────────────────────────────────────────────────────────────────────────── */

const first_purchase_discount_reminder = {
  nodes: [
    trigger(
      "Webhook: Klaviyo Subscriber Received Discount Code",
      "webhook_app",
      {
        source: "klaviyo",
        event: "discount_code_assigned",
        howItStarts:
          "Fires when a new subscriber is assigned a first-purchase discount code through Klaviyo's signup flow or coupon distribution.",
        setup:
          "Configure Klaviyo to send a webhook when a profile receives a unique discount code via a welcome flow or signup form. Include the code, expiry date, and discount percentage.",
        inputFields: [],
      },
    ),
    aiNode(
      "2",
      "AI Extract Discount Details and Subscriber Profile",
      280,
      "You are a conversion optimization specialist. Extract the subscriber email, first name, discount code, discount percentage, expiry date, and signup date. Calculate the days remaining until expiry. Output valid JSON.",
      "Parse this discount assignment event and extract all coupon details: {webhook_payload}",
      [
        { field: "subscriber_email", as: "subscriber_email", type: "string" },
        { field: "first_name", as: "first_name", type: "string" },
        { field: "discount_code", as: "discount_code", type: "string" },
        { field: "discount_pct", as: "discount_pct", type: "string" },
        { field: "expiry_date", as: "expiry_date", type: "string" },
        { field: "days_remaining", as: "days_remaining", type: "number" },
      ],
    ),
    n("3", "hitApi", "Klaviyo: Check If First Purchase Already Made", 520, {
      hitApiDetails: {
        method: "GET",
        url: 'https://a.klaviyo.com/api/profiles/?filter=equals(email,"{subscriber_email}")',
        headers: [
          {
            key: "Authorization",
            value: "Klaviyo-API-Key {klaviyo_private_key}",
          },
          { key: "Content-Type", value: "application/json" },
          { key: "revision", value: "2024-02-15" },
        ],
        body: [],
        variables: [
          {
            name: "data[0].attributes.properties.total_orders",
            customName: "total_orders",
            type: "number",
          },
          {
            name: "data[0].attributes.properties.last_browse_category",
            customName: "last_browse_category",
            type: "string",
          },
          {
            name: "data[0].id",
            customName: "klaviyo_profile_id",
            type: "string",
          },
        ],
      },
    }),
    n("4", "condition", "Has Not Made First Purchase Yet?", 760, {
      conditionDetails: {
        conditions: [
          { variable: "{total_orders}", operator: "equals", value: "0" },
        ],
      },
    }),
    emailNode(
      "5",
      "Email: Discount Reminder with Curated Picks",
      1000,
      "{subscriber_email}",
      "Reminder: your {discount_pct}% off code expires soon, {first_name}",
      "Hi {first_name},\\n\\nJust a friendly reminder — your exclusive {discount_pct}% off discount is waiting for you!\\n\\nCode: {discount_code}\\nExpires: {expiry_date}\\n\\nNot sure what to get? Here are some popular picks in {last_browse_category}:\\n\\n- Our top-rated product in this category\\n- This week's staff pick\\n- New arrival you might love\\n\\nUse your code at checkout: https://shop.example.com?code={discount_code}\\n\\nDo not let this one slip away!",
    ),
    n("6", "delayAction", "Wait 3 Days Before Expiry Urgency", 1240, {
      delayDetails: { duration: 3, unit: "days" },
    }),
    n("7", "condition", "Still No Purchase and Code Not Expired?", 1480, {
      conditionDetails: {
        conditions: [
          {
            variable: "{days_remaining}",
            operator: "greater_than",
            value: "0",
          },
        ],
      },
    }),
    emailNode(
      "8",
      "Email: Last Day Urgency with Social Proof",
      1720,
      "{subscriber_email}",
      "LAST CHANCE: {discount_pct}% off expires tomorrow, {first_name}!",
      "Hi {first_name},\\n\\nThis is your last chance — your {discount_pct}% off discount code {discount_code} expires tomorrow!\\n\\nThousands of customers have used their welcome discount to discover their new favorite products. Do not miss out!\\n\\nWhat our customers say:\\n'I wish I had ordered sooner!' — Sarah T.\\n'Best quality for the price, especially with the welcome discount.' — Mike R.\\n\\nShop now before it expires: https://shop.example.com?code={discount_code}\\n\\nAfter tomorrow, this code will no longer work.",
      400,
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5", "output_1"),
    e("5", "6"),
    e("6", "7"),
    e("7", "8", "output_1"),
  ],
  summary:
    "Klaviyo first-purchase discount reminder flow triggered when a subscriber receives a welcome discount code. AI extracts coupon details including expiry and days remaining. The Klaviyo API verifies the subscriber has not yet purchased. Those without a first purchase get a reminder email with curated picks based on their browse history. Three days later, a last-chance urgency email with social proof testimonials creates final pressure before the code expires.",
};

/* ───────────────────────────────────────────────────────────────────────────
 * 36. ABANDONED CART REMINDER — Klaviyo | Pattern B (Cart Abandonment)
 * ─────────────────────────────────────────────────────────────────────────── */

const abandoned_cart_reminder = {
  nodes: [
    trigger("Webhook: Klaviyo Cart Abandoned Event", "webhook_app", {
      source: "klaviyo",
      event: "cart_abandoned",
      howItStarts:
        "Fires when Klaviyo detects that a customer added items to their cart but left without completing checkout after the defined abandonment window.",
      setup:
        "Configure Klaviyo to send a webhook on the Added to Cart metric when no Placed Order event follows within 1 hour. Include cart contents, totals, and customer profile.",
      inputFields: [],
    }),
    aiNode(
      "2",
      "AI Extract Cart Details and Product Information",
      280,
      "You are a cart recovery specialist. Extract the customer email, first name, cart total, cart currency, number of items, top product name, top product image URL, top product price, and the full cart URL. Output valid JSON.",
      "Parse this abandoned cart event and extract all cart and product details: {webhook_payload}",
      [
        { field: "customer_email", as: "customer_email", type: "string" },
        { field: "first_name", as: "first_name", type: "string" },
        { field: "cart_total", as: "cart_total", type: "string" },
        { field: "item_count", as: "item_count", type: "number" },
        { field: "top_product_name", as: "top_product_name", type: "string" },
        { field: "top_product_price", as: "top_product_price", type: "string" },
        { field: "cart_url", as: "cart_url", type: "string" },
      ],
    ),
    n(
      "3",
      "hitApi",
      "Klaviyo: Lookup Customer Purchase History and Segments",
      520,
      {
        hitApiDetails: {
          method: "GET",
          url: 'https://a.klaviyo.com/api/profiles/?filter=equals(email,"{customer_email}")',
          headers: [
            {
              key: "Authorization",
              value: "Klaviyo-API-Key {klaviyo_private_key}",
            },
            { key: "Content-Type", value: "application/json" },
            { key: "revision", value: "2024-02-15" },
          ],
          body: [],
          variables: [
            {
              name: "data[0].attributes.properties.total_orders",
              customName: "total_orders",
              type: "number",
            },
            {
              name: "data[0].attributes.properties.average_order_value",
              customName: "avg_order_value",
              type: "number",
            },
            {
              name: "data[0].attributes.properties.last_purchase_date",
              customName: "last_purchase_date",
              type: "string",
            },
          ],
        },
      },
    ),
    n("4", "condition", "Cart Value Over $100?", 760, {
      conditionDetails: {
        conditions: [
          { variable: "{cart_total}", operator: "greater_than", value: "100" },
        ],
      },
    }),
    emailNode(
      "5",
      "Email: Cart Reminder with Product Details",
      1000,
      "{customer_email}",
      "You left something behind, {first_name}",
      "Hi {first_name},\\n\\nLooks like you left some great items in your cart!\\n\\nYour cart ({item_count} items, ${cart_total}):\\n- {top_product_name} — ${top_product_price}\\n- Plus {item_count} more items\\n\\nYour cart is saved and ready for you: {cart_url}\\n\\nThese items are popular and may sell out. Complete your order before they are gone!\\n\\nNeed help? Reply to this email and our team will assist you.",
    ),
    n("6", "delayAction", "Wait 12 Hours for Purchase", 1240, {
      delayDetails: { duration: 12, unit: "hours" },
    }),
    n("7", "condition", "Still Not Purchased?", 1480, {
      conditionDetails: {
        conditions: [
          { variable: "{last_purchase_date}", operator: "equals", value: "" },
        ],
      },
    }),
    emailNode(
      "8",
      "Email: Second Reminder with Social Proof",
      1720,
      "{customer_email}",
      "Still thinking about it, {first_name}? Others love {top_product_name}",
      "Hi {first_name},\\n\\nYour cart is still waiting — and it turns out, you have great taste!\\n\\n{top_product_name} is one of our most popular products with hundreds of 5-star reviews.\\n\\nWhat customers are saying:\\n'Absolutely worth it — exceeded my expectations'\\n'Best purchase I have made this year'\\n\\nYour cart (${cart_total}): {cart_url}\\n\\nFree shipping on orders over $75 — your cart qualifies!\\n\\nComplete your order now before stock runs low.",
      400,
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5", "output_1"),
    e("5", "6"),
    e("6", "7"),
    e("7", "8", "output_1"),
  ],
  summary:
    "Klaviyo abandoned cart recovery flow triggered when a customer abandons their cart. AI extracts cart contents including product details and totals, then the Klaviyo API checks purchase history and average order value. Carts over $100 receive a first reminder email highlighting the saved products and urgency. After 12 hours, those who still have not purchased get a second email with social proof (customer reviews) and a free shipping qualifier to drive completion.",
};

/* ───────────────────────────────────────────────────────────────────────────
 * 37. HIGH AOV CART RESCUE — Klaviyo | Pattern B (Cart Abandonment)
 * ─────────────────────────────────────────────────────────────────────────── */

const high_aov_cart_rescue = {
  nodes: [
    trigger("Webhook: Klaviyo High-Value Cart Abandoned", "webhook_app", {
      source: "klaviyo",
      event: "high_value_cart_abandoned",
      howItStarts:
        "Fires when Klaviyo detects a cart abandonment where the total exceeds the high-value threshold ($250+), triggering a premium recovery experience.",
      setup:
        "Configure Klaviyo to send a webhook on cart abandonment events where cart value exceeds $250. Include full cart contents, customer profile, and browse history.",
      inputFields: [],
    }),
    aiNode(
      "2",
      "AI Extract High-Value Cart Details and Customer Profile",
      280,
      "You are a premium customer recovery specialist. Extract the customer email, first name, cart total, top 3 product names (comma-separated), cart item count, cart URL, and the most expensive item name and price. Also determine the customer segment (vip, repeat, first_timer) based on available profile data. Output valid JSON.",
      "Analyze this high-value abandoned cart and extract premium recovery details: {webhook_payload}",
      [
        { field: "customer_email", as: "customer_email", type: "string" },
        { field: "first_name", as: "first_name", type: "string" },
        { field: "cart_total", as: "cart_total", type: "string" },
        { field: "top_products", as: "top_products", type: "string" },
        { field: "item_count", as: "item_count", type: "number" },
        { field: "cart_url", as: "cart_url", type: "string" },
        {
          field: "most_expensive_item",
          as: "most_expensive_item",
          type: "string",
        },
        { field: "customer_segment", as: "customer_segment", type: "string" },
      ],
    ),
    n(
      "3",
      "hitApi",
      "Klaviyo: Fetch Customer VIP Status and Lifetime Spend",
      520,
      {
        hitApiDetails: {
          method: "GET",
          url: 'https://a.klaviyo.com/api/profiles/?filter=equals(email,"{customer_email}")',
          headers: [
            {
              key: "Authorization",
              value: "Klaviyo-API-Key {klaviyo_private_key}",
            },
            { key: "Content-Type", value: "application/json" },
            { key: "revision", value: "2024-02-15" },
          ],
          body: [],
          variables: [
            {
              name: "data[0].attributes.properties.lifetime_value",
              customName: "lifetime_value",
              type: "number",
            },
            {
              name: "data[0].attributes.properties.total_orders",
              customName: "total_orders",
              type: "number",
            },
            {
              name: "data[0].attributes.properties.vip_status",
              customName: "vip_status",
              type: "string",
            },
          ],
        },
      },
    ),
    n("4", "condition", "Cart Value Over $500 (Ultra-Premium)?", 760, {
      conditionDetails: {
        conditions: [
          { variable: "{cart_total}", operator: "greater_than", value: "500" },
        ],
      },
    }),
    emailNode(
      "5",
      "Email: Premium Cart Recovery with Concierge Offer",
      1000,
      "{customer_email}",
      "Your curated selection is waiting, {first_name}",
      "Hi {first_name},\\n\\nWe noticed you were exploring some of our finest products — excellent taste!\\n\\nYour curated selection (${cart_total}):\\n{top_products}\\n\\nFeatured: {most_expensive_item}\\n\\nAs a valued customer, we would like to offer you a premium experience:\\n- Complimentary express shipping on this order\\n- Personal styling consultation (reply to schedule)\\n- Extended 60-day return window\\n\\nComplete your order: {cart_url}\\n\\nQuestions about sizing, materials, or styling? Our concierge team is standing by — just reply to this email.",
    ),
    n("6", "delayAction", "Wait 4 Hours for Premium Response Window", 1240, {
      delayDetails: { duration: 4, unit: "hours" },
    }),
    n("7", "condition", "Still Not Purchased?", 1480, {
      conditionDetails: {
        conditions: [
          { variable: "{total_orders}", operator: "greater_than", value: "2" },
        ],
      },
    }),
    emailNode(
      "8",
      "Email: Personal Touch with Exclusive Incentive",
      1720,
      "{customer_email}",
      "A personal note about your cart, {first_name}",
      "Hi {first_name},\\n\\nI am reaching out personally because I noticed you had a wonderful selection in your cart.\\n\\nI understand that a ${cart_total} purchase deserves some thought. I wanted to share a few things that might help:\\n\\n1. Each of our products comes with a quality guarantee\\n2. Free returns within 60 days — no questions asked\\n3. As a special courtesy, use code VIP10 for an additional 10% off your cart\\n\\nYour saved cart: {cart_url}\\n\\nWould you like me to walk you through anything? I am happy to help with product details, sizing, or recommendations.\\n\\nBest regards,\\nYour Personal Shopping Advisor",
      400,
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5", "output_1"),
    e("5", "6"),
    e("6", "7"),
    e("7", "8", "output_2"),
  ],
  summary:
    "Premium Klaviyo cart rescue flow for high-value carts ($250+). AI extracts detailed cart contents and customer segmentation, then the Klaviyo API checks lifetime value and VIP status. Ultra-premium carts ($500+) receive a concierge-style recovery email with complimentary express shipping, personal styling, and extended returns. After a 4-hour premium response window, repeat customers who have not converted get a personal-touch email from a shopping advisor with an exclusive VIP10 discount code.",
};

/* ───────────────────────────────────────────────────────────────────────────
 * 38. STARTED CHECKOUT RECOVERY — Klaviyo | Pattern B (Cart Abandonment)
 * ─────────────────────────────────────────────────────────────────────────── */

const started_checkout_recovery = {
  nodes: [
    trigger(
      "Webhook: Klaviyo Checkout Started but Not Completed",
      "webhook_app",
      {
        source: "klaviyo",
        event: "checkout_started",
        howItStarts:
          "Fires when Klaviyo detects that a customer started the checkout process (entered shipping or payment info) but did not complete the order within 30 minutes.",
        setup:
          "Configure Klaviyo to send a webhook on the Started Checkout metric when no Placed Order follows within 30 minutes. Include checkout contents, step reached, and customer details.",
        inputFields: [],
      },
    ),
    aiNode(
      "2",
      "AI Extract Checkout Details and Abandonment Point",
      280,
      "You are a checkout optimization specialist. Extract the customer email, first name, checkout total, item count, checkout URL, the furthest checkout step reached (shipping, payment, review), shipping address city, and the payment method attempted if available. Also identify the likely abandonment reason (shipping_cost, payment_issue, comparison_shopping, distraction, coupon_hunting). Output valid JSON.",
      "Analyze this abandoned checkout event and determine the abandonment point: {webhook_payload}",
      [
        { field: "customer_email", as: "customer_email", type: "string" },
        { field: "first_name", as: "first_name", type: "string" },
        { field: "checkout_total", as: "checkout_total", type: "string" },
        { field: "item_count", as: "item_count", type: "number" },
        { field: "checkout_url", as: "checkout_url", type: "string" },
        { field: "checkout_step", as: "checkout_step", type: "string" },
        { field: "likely_reason", as: "likely_reason", type: "string" },
        { field: "shipping_city", as: "shipping_city", type: "string" },
      ],
    ),
    n("3", "hitApi", "Klaviyo: Fetch Customer Profile and Order History", 520, {
      hitApiDetails: {
        method: "GET",
        url: 'https://a.klaviyo.com/api/profiles/?filter=equals(email,"{customer_email}")',
        headers: [
          {
            key: "Authorization",
            value: "Klaviyo-API-Key {klaviyo_private_key}",
          },
          { key: "Content-Type", value: "application/json" },
          { key: "revision", value: "2024-02-15" },
        ],
        body: [],
        variables: [
          {
            name: "data[0].attributes.properties.total_orders",
            customName: "total_orders",
            type: "number",
          },
          {
            name: "data[0].attributes.properties.last_purchase_date",
            customName: "last_purchase_date",
            type: "string",
          },
          {
            name: "data[0].attributes.properties.average_order_value",
            customName: "avg_order_value",
            type: "number",
          },
        ],
      },
    }),
    n("4", "condition", "Reached Payment Step?", 760, {
      conditionDetails: {
        conditions: [
          { variable: "{checkout_step}", operator: "equals", value: "payment" },
        ],
      },
    }),
    emailNode(
      "5",
      "Email: Checkout Recovery Addressing Specific Drop-Off",
      1000,
      "{customer_email}",
      "Your order is almost complete, {first_name}!",
      "Hi {first_name},\\n\\nYou were so close! Your checkout for ${checkout_total} ({item_count} items) is still saved and ready to complete.\\n\\nWe noticed you got to the {checkout_step} step — here is what might help:\\n\\n- Secure checkout: Your payment info is encrypted and protected\\n- Free shipping: Available on orders over $75\\n- Easy returns: 30-day hassle-free return policy\\n- Multiple payment options: Credit card, PayPal, Apple Pay, and more\\n\\nResume your checkout: {checkout_url}\\n\\nEverything is saved exactly where you left off — just pick up where you stopped.\\n\\nNeed help completing your order? Reply and we will assist you right away.",
    ),
    n("6", "delayAction", "Wait 6 Hours for Checkout Completion", 1240, {
      delayDetails: { duration: 6, unit: "hours" },
    }),
    n("7", "condition", "Order Still Not Placed?", 1480, {
      conditionDetails: {
        conditions: [
          { variable: "{last_purchase_date}", operator: "equals", value: "" },
        ],
      },
    }),
    emailNode(
      "8",
      "Email: Final Recovery with Targeted Incentive",
      1720,
      "{customer_email}",
      "We saved your cart, {first_name} — plus a little something extra",
      "Hi {first_name},\\n\\nWe know life gets busy, so we saved your checkout for you.\\n\\nYour order summary: ${checkout_total} ({item_count} items)\\nShipping to: {shipping_city}\\n\\nTo make it easier, here is a special offer just for you:\\nUse code FINISH10 for 10% off this order\\n\\nThat brings your total to just $" +
        "{checkout_total} minus 10%.\\n\\nThis code expires in 24 hours and applies only to your current checkout.\\n\\nComplete your order now: {checkout_url}\\n\\nWe would hate for you to miss out on these items!",
      400,
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5", "output_1"),
    e("5", "6"),
    e("6", "7"),
    e("7", "8", "output_1"),
  ],
  summary:
    "Klaviyo checkout recovery flow triggered when a customer starts checkout but does not complete. AI identifies the exact drop-off step (shipping, payment, review) and likely abandonment reason. The Klaviyo API fetches order history for context. A first recovery email addresses the specific step where the customer stopped with reassurance (security, returns, payment options). After 6 hours, those who still have not ordered receive a final email with a targeted FINISH10 discount code and the shipping city to reinforce the near-complete status of their order.",
};

/* ═══════════════════════════════════════════════════════════════════════════
 * EXPORT
 * ═══════════════════════════════════════════════════════════════════════════ */

export const ELITE_BATCH22_PART3: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  "Referral Invite Journey": referral_invite_journey,
  "Quiz Result Personalization": quiz_result_personalization,
  "App Uninstall Rescue": app_uninstall_rescue,
  "Push-to-Email Fallback Journey": push_to_email_fallback_journey,
  "Festival Sale Countdown": festival_sale_countdown,
  "Order Shipment + Feedback Journey": order_shipment_feedback_journey,
  "RFM High-Value Customer Nurture": rfm_high_value_customer_nurture,
  "Welcome Series Email": welcome_series_email,
  "Welcome Series Email + SMS": welcome_series_email_sms,
  "First Purchase Discount Reminder": first_purchase_discount_reminder,
  "Abandoned Cart Reminder": abandoned_cart_reminder,
  "High AOV Cart Rescue": high_aov_cart_rescue,
  "Started Checkout Recovery": started_checkout_recovery,
};
